/**
 * Thin client for tetris_gm session APIs (Option B).
 */
import { ARCADE, API_PATH_PROXY_ENABLED } from '../../../utils/config';
import { acquireGmLease, endGmSession, noteGmState } from '../gmLease';

/** Lease key for this game's shared GM (game_engines/gm_registry.py). */
export const GM_GAME_ID = 'tetris';

export const TETRIS_GM_BASE = API_PATH_PROXY_ENABLED
    ? (ARCADE.tetris?.API_BASE || '')
    : '/tetris';

async function parseJson(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `Tetris GM HTTP ${res.status}`);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return noteGmState(GM_GAME_ID, data);
}

export async function createTetrisSession() {
    await acquireGmLease(GM_GAME_ID);
    const res = await fetch(`${TETRIS_GM_BASE}/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
    });
    return parseJson(res);
}

/** `action` is one action, or an array of queued ticks applied in order (tickBatcher.js). */
export async function moveTetrisSession(sessionId, action = 'stay') {
    const res = await fetch(`${TETRIS_GM_BASE}/session/${sessionId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.isArray(action) ? { actions: action } : { action }),
    });
    return parseJson(res);
}

/** Server cap on actions per /move (tetris_gm.MAX_BATCH_ACTIONS). */
export const MAX_BATCH_ACTIONS = 32;

export async function deleteTetrisSession(sessionId) {
    if (!sessionId) return;
    endGmSession(GM_GAME_ID, sessionId);
    try {
        await fetch(`${TETRIS_GM_BASE}/session/${sessionId}/delete`, { method: 'POST' });
    } catch {
        /* best-effort */
    }
}

/** GM piece kind 1–7 → canvas color (I O T S Z J L). */
export const KIND_COLORS = {
    // Stackfall palette (see visuals/TetrisScene STACKFALL_PALETTE)
    1: '#5C7FA8',
    2: '#C9B48A',
    3: '#9A5C8F',
    4: '#7FA05A',
    5: '#D0705E',
    6: '#3FA796',
    7: '#D49A3A',
};

export function boardKindsToColors(board) {
    if (!Array.isArray(board)) return [];
    return board.map((row) => row.map((cell) => (cell ? (KIND_COLORS[cell] || '#888') : 0)));
}

/** Map GM piece payload → NextPiecePreview / TetrisScene shape. */
export function pieceForPreview(piece) {
    if (!piece || !Array.isArray(piece.shape)) return null;
    const kind = typeof piece.type === 'string'
        ? ({ I: 1, O: 2, T: 3, S: 4, Z: 5, J: 6, L: 7 }[piece.type] || 1)
        : 1;
    return {
        type: piece.type,
        shape: piece.shape,
        x: piece.x ?? 0,
        y: piece.y ?? 0,
        color: KIND_COLORS[kind] || '#888',
    };
}
